import React, { useCallback, useEffect, useState, createContext, useContext, useRef } from "react";
import { Segment, GlossaryEntry, Document, SegmentStatus } from "../data/mockData";
import { fetchTemplates, fetchTemplateBodies, groupIntoPairs, DocumentPair } from "../data/api";
import { parseLexicalBody, matchTranslations, segmentsToInjectionTexts } from "../data/lexicalParser";
import { getStoredApiKey, setStoredApiKey, ModelConfig, DEFAULT_MODELS, normalizeModelConfigs, modelConfigsNeedNormalization } from "../data/translationService";
import { StoredSegmentState, SyncReport, syncSegmentsWithStored, storedStatesFromLegacyOverrides } from "../data/segmentSync";
import { hashSourceText } from "../data/contentHash";
import { fetchWorkspaceSettings, saveWorkspaceSettings, fetchAllDocumentStates, fetchDocumentState, upsertDocumentState, DocumentStateRecord } from "../data/translationStateApi";
import { bulkApproveTranslatedTemplatePairs, BulkApproveReport, TranslatedTemplatePair } from "../data/bulkApproveFromFr";
import { CachedSegmentWork, getSegmentDraftCache, withSegmentDraftCache, withoutSegmentDraftCache } from "../data/serverDraftCache";
const LEGACY_GLOSSARY_STORAGE_KEY = "lawvo_glossary";
const LEGACY_MODELS_STORAGE_KEY = "lawvo_model_configs";
const LEGACY_SEGMENT_OVERRIDES_KEY = "lawvo_segment_overrides";
const LEGACY_DRAFT_CACHE_KEY = "lawvo_draft_cache";
const LEGACY_API_KEY_STORAGE_KEY = "lawvo_openrouter_api_key";
const MIGRATION_FLAG_KEY = "lawvo_state_migrated_to_server";
export interface DocSyncStats extends SyncReport {
  matched: number;
  edited: number;
  added: number;
  removed: number;
}
function reportToDocStats(report: SyncReport): DocSyncStats {
  return {
    ...report,
    matched: report.unchanged,
    edited: report.changedSections,
    added: report.newSections,
    removed: report.removedSections
  };
}
interface TranslationContextType {
  documents: Document[];
  currentDocument: Document | undefined;
  setCurrentDocumentId: (id: string) => void;
  segments: Segment[];
  glossary: GlossaryEntry[];
  updateSegmentStatus: (id: string, status: SegmentStatus, mergedText?: string, options?: {
    lock?: boolean;
  }) => void;
  unlockSegment: (id: string) => void;
  splitSection: (id: string, splitAt: number) => void;
  addGlossaryEntry: (entry: Omit<GlossaryEntry, "id">) => void;
  updateGlossaryEntry: (id: string, entry: Partial<GlossaryEntry>) => void;
  deleteGlossaryEntry: (id: string) => void;
  documentTitle: string;
  addDocument: (title: string, sourceText: string) => string;
  isLoading: boolean;
  error: string | null;
  refreshDocuments: () => Promise<Map<string, DocSyncStats>>;
  syncDocumentFromServer: (docId: string) => Promise<DocSyncStats | null>;
  lastSyncReport: SyncReport | null;
  openRouterApiKey: string;
  setOpenRouterApiKey: (key: string) => void;
  modelConfigs: ModelConfig[];
  setModelConfigs: (configs: ModelConfig[]) => void;
  setGlossaryFromBackup: (entries: GlossaryEntry[]) => void;
  segmentOverrides: Record<string, StoredSegmentState>;
  setSegmentOverridesFromBackup: (overrides: Record<string, {
    status: SegmentStatus;
    mergedText?: string;
    sourceText?: string;
  }>) => void;
  restoreDocumentStatesFromBackup: (states: Record<string, StoredSegmentState[]>) => void;
  bulkApproveTranslatedTemplates: (pairs: TranslatedTemplatePair[], options?: {
    onProgress?: (info: {
      current: number;
      total: number;
      title: string;
    }) => void;
  }) => Promise<BulkApproveReport>;
  getSegmentDraftCache: (segmentId: string) => CachedSegmentWork | null;
  saveSegmentDraftCache: (segmentId: string, work: CachedSegmentWork) => void;
  clearSegmentDraftCache: (segmentId: string) => void;
  clearAllTranslations: () => void;
  documentStates: Record<string, StoredSegmentState[]>;
  persistDocumentState: (docId: string) => Promise<void>;
}
const TranslationContext = createContext<TranslationContextType | undefined>(undefined);
function pairToBaseDocument(pair: DocumentPair): Document {
  const enBody = pair.enTemplate.body;
  const frBody = pair.frTemplate?.body || null;
  const enSegments = parseLexicalBody(enBody);
  const frMatches = matchTranslations(enSegments, frBody);
  const translatable = enSegments.filter(s => s.isTranslatable);
  const segments: Segment[] = translatable.map((seg, index) => ({
    id: `pending-${index}`,
    sectionId: `pending-${index}`,
    structuralPath: seg.structuralPath,
    sourceHash: hashSourceText(seg.text),
    clauseId: seg.type === "heading" ? seg.text : `Section ${index + 1}`,
    sourceText: seg.text,
    status: "not_started" as const,
    existingTranslation: frMatches[index],
    injectionIndex: seg.injectionIndex
  }));
  return {
    id: pair.id,
    title: pair.title,
    createdAt: pair.createdAt,
    sourceLanguage: pair.enTemplate.locale === "fr" ? "FR" : "EN",
    targetLanguage: pair.enTemplate.locale === "fr" ? "EN" : "FR",
    segments,
    status: "translating",
    isActive: pair.enTemplate.active,
    enBody,
    frBody,
    templateId: pair.enTemplate.id,
    frTemplateId: pair.frTemplate?.id || null,
    locales: [...(pair.enTemplate.locale === "en" ? ["EN"] : []), ...(pair.enTemplate.locale === "fr" ? ["FR"] : []), ...(pair.frTemplate ? ["FR"] : [])].filter((v, i, a) => a.indexOf(v) === i)
  };
}
function deriveDocumentStatus(segments: Segment[]): Document["status"] {
  const active = segments.filter(s => s.status !== "removed");
  const approvedCount = active.filter(s => s.status === "approved").length;
  if (approvedCount === active.length && active.length > 0) return "completed";
  if (approvedCount === 0 && !active.some(s => s.status === "in_progress")) {
    return "translating";
  }
  return "in_review";
}
export const TranslationProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [currentDocumentId, setCurrentDocumentId] = useState<string | null>(null);
  const [glossary, setGlossary] = useState<GlossaryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openRouterApiKey, setOpenRouterApiKeyState] = useState<string>("");
  const [modelConfigs, setModelConfigsState] = useState<ModelConfig[]>(DEFAULT_MODELS);
  const [documentStates, setDocumentStates] = useState<Record<string, StoredSegmentState[]>>({});
  const [draftCaches, setDraftCaches] = useState<Record<string, Record<string, unknown>>>({});
  const [lastSyncReport, setLastSyncReport] = useState<SyncReport | null>(null);
  const stateRecordsRef = useRef<Record<string, DocumentStateRecord>>({});
  const documentStatesRef = useRef(documentStates);
  documentStatesRef.current = documentStates;
  const saveWorkspaceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const workspaceSettingsHydratedRef = useRef(false);
  const persistWorkspaceSettings = useCallback(async (g: GlossaryEntry[], models: ModelConfig[], apiKey = openRouterApiKey) => {
    await saveWorkspaceSettings({
      glossary: g,
      modelConfigs: models,
      openRouterApiKey: apiKey
    });
  }, [openRouterApiKey]);
  const persistDocumentState = useCallback(async (docId: string, overrides?: {
    segments?: StoredSegmentState[];
    draftCache?: Record<string, unknown>;
  }) => {
    const doc = documents.find(d => d.id === docId);
    if (!doc?.templateId) return;
    const states = overrides?.segments ?? documentStatesRef.current[docId] ?? [];
    const draftCache = overrides?.draftCache ?? draftCaches[docId] ?? {};
    try {
      const record = await upsertDocumentState({
        documentPairKey: docId,
        enTemplateId: doc.templateId,
        frTemplateId: doc.frTemplateId,
        segments: states.filter(s => s.status !== "removed"),
        draftCache
      });
      stateRecordsRef.current[docId] = record;
    } catch (err) {
      console.warn(`Failed to persist document state for ${docId}:`, err);
    }
  }, [documents, documentStates, draftCaches]);
  const migrateLocalToServer = useCallback(async () => {
    if (localStorage.getItem(MIGRATION_FLAG_KEY) === "true") return;
    try {
      const readJson = <T,>(key: string): T | null => {
        try {
          const raw = localStorage.getItem(key);
          return raw ? JSON.parse(raw) as T : null;
        } catch {
          return null;
        }
      };
      const localGlossary = readJson<GlossaryEntry[]>(LEGACY_GLOSSARY_STORAGE_KEY);
      const localModels = readJson<ModelConfig[]>(LEGACY_MODELS_STORAGE_KEY);
      const legacyOverrides = readJson<Record<string, {
        status: SegmentStatus;
        mergedText?: string;
        sourceText?: string;
      }>>(LEGACY_SEGMENT_OVERRIDES_KEY) || {};
      const legacyApiKey = localStorage.getItem(LEGACY_API_KEY_STORAGE_KEY) || "";
      const existing = await fetchWorkspaceSettings();
      const shouldSeedGlossary = !existing?.glossary?.length && !!localGlossary?.length;
      const shouldSeedModels = !existing?.modelConfigs?.length && localModels?.length === 3;
      const shouldSeedApiKey = !existing?.openRouterApiKey && legacyApiKey;
      if (shouldSeedGlossary || shouldSeedModels || shouldSeedApiKey) {
        await saveWorkspaceSettings({
          glossary: shouldSeedGlossary ? localGlossary : existing?.glossary || [],
          modelConfigs: shouldSeedModels ? localModels : existing?.modelConfigs || DEFAULT_MODELS,
          openRouterApiKey: shouldSeedApiKey ? legacyApiKey : existing?.openRouterApiKey
        });
      }
      if (Object.keys(legacyOverrides).length > 0) {
        const allStates = await fetchAllDocumentStates();
        if (allStates.length === 0) {
          const templates = await fetchTemplates();
          const pairs = groupIntoPairs(templates);
          for (const pair of pairs) {
            const base = pairToBaseDocument(pair);
            const states = storedStatesFromLegacyOverrides(pair.id, base.segments, legacyOverrides);
            if (states.some(s => s.mergedText || s.status !== "not_started")) {
              await upsertDocumentState({
                documentPairKey: pair.id,
                enTemplateId: pair.enTemplate.id,
                frTemplateId: pair.frTemplate?.id,
                segments: states,
                draftCache: {}
              });
            }
          }
        }
      }
      localStorage.removeItem(LEGACY_GLOSSARY_STORAGE_KEY);
      localStorage.removeItem(LEGACY_MODELS_STORAGE_KEY);
      localStorage.removeItem(LEGACY_SEGMENT_OVERRIDES_KEY);
      localStorage.removeItem(LEGACY_DRAFT_CACHE_KEY);
      localStorage.removeItem(LEGACY_API_KEY_STORAGE_KEY);
      localStorage.setItem(MIGRATION_FLAG_KEY, "true");
    } catch (err) {
      console.warn("Local-to-server migration skipped:", err);
    }
  }, []);
  const loadDocuments = useCallback(async (): Promise<Map<string, DocSyncStats>> => {
    setIsLoading(true);
    setError(null);
    const statsByDoc = new Map<string, DocSyncStats>();
    try {
      await migrateLocalToServer();
      const [templates, workspaceSettings, allDocStates] = await Promise.all([fetchTemplates(), fetchWorkspaceSettings().catch(() => null), fetchAllDocumentStates().catch(() => [] as DocumentStateRecord[])]);
      if (workspaceSettings?.glossary?.length) {
        setGlossary(workspaceSettings.glossary);
      }
      if (workspaceSettings?.modelConfigs?.length === 3) {
        const normalizedModels = normalizeModelConfigs(workspaceSettings.modelConfigs);
        setModelConfigsState(normalizedModels);
        if (modelConfigsNeedNormalization(workspaceSettings.modelConfigs)) {
          saveWorkspaceSettings({
            ...workspaceSettings,
            modelConfigs: normalizedModels
          }).catch(err => {
            console.warn("Failed to migrate workspace model IDs on server:", err);
          });
        }
      }
      if (workspaceSettings?.openRouterApiKey) {
        setOpenRouterApiKeyState(workspaceSettings.openRouterApiKey);
        setStoredApiKey(workspaceSettings.openRouterApiKey);
      }
      workspaceSettingsHydratedRef.current = true;
      const statesByDoc: Record<string, StoredSegmentState[]> = {};
      const cachesByDoc: Record<string, Record<string, unknown>> = {};
      const recordsByDoc: Record<string, DocumentStateRecord> = {};
      for (const record of allDocStates) {
        statesByDoc[record.documentPairKey] = record.segments || [];
        cachesByDoc[record.documentPairKey] = record.draftCache || {};
        recordsByDoc[record.documentPairKey] = record;
      }
      stateRecordsRef.current = recordsByDoc;
      setDocumentStates(statesByDoc);
      setDraftCaches(cachesByDoc);
      const pairs = groupIntoPairs(templates);
      const docs = await Promise.all(pairs.map(async pair => {
        let enBody = pair.enTemplate.body;
        let frBody = pair.frTemplate?.body ?? null;
        if (!enBody?.trim()) {
          const fresh = await fetchTemplateBodies(pair.enTemplate.id, pair.frTemplate?.id);
          enBody = fresh.enBody;
          frBody = fresh.frBody;
        }
        const parsedEn = parseLexicalBody(enBody);
        const enSegments = parsedEn.filter(s => s.isTranslatable);
        const frMatches = matchTranslations(parsedEn, frBody);
        const stored = statesByDoc[pair.id] || [];
        const {
          segments,
          states,
          report
        } = syncSegmentsWithStored(enSegments, stored, frMatches);
        statsByDoc.set(pair.id, reportToDocStats(report));
        const activeStates = states.filter(s => s.status !== "removed");
        statesByDoc[pair.id] = activeStates;
        const base = pairToBaseDocument(pair);
        return {
          ...base,
          enBody,
          frBody,
          segments: segments.filter(s => s.status !== "removed"),
          status: deriveDocumentStatus(segments)
        };
      }));
      setDocumentStates(statesByDoc);
      setDocuments(docs);
    } catch (err) {
      console.error("Failed to fetch templates:", err);
      setError(err instanceof Error ? err.message : "Failed to load documents");
    } finally {
      setIsLoading(false);
    }
    return statsByDoc;
  }, [migrateLocalToServer]);
  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);
  useEffect(() => {
    if (!workspaceSettingsHydratedRef.current) return;
    if (saveWorkspaceTimer.current) clearTimeout(saveWorkspaceTimer.current);
    saveWorkspaceTimer.current = setTimeout(() => {
      persistWorkspaceSettings(glossary, modelConfigs, openRouterApiKey).catch(err => {
        console.warn("Failed to save workspace settings to server:", err);
      });
    }, 800);
    return () => {
      if (saveWorkspaceTimer.current) clearTimeout(saveWorkspaceTimer.current);
    };
  }, [glossary, modelConfigs, openRouterApiKey, persistWorkspaceSettings]);
  const handleSetApiKey = (key: string) => {
    setOpenRouterApiKeyState(key);
    setStoredApiKey(key);
    persistWorkspaceSettings(glossary, modelConfigs, key).catch(err => {
      console.warn("Failed to save API key to server:", err);
    });
  };
  const handleSetModelConfigs = (configs: ModelConfig[]) => {
    setModelConfigsState(normalizeModelConfigs(configs));
  };
  const currentDocument = documents.find(d => d.id === currentDocumentId);
  const segments = currentDocument?.segments || [];
  const documentTitle = currentDocument?.title || "";
  const updateSegmentStatus = (id: string, status: SegmentStatus, mergedText?: string, options?: {
    lock?: boolean;
  }) => {
    if (!currentDocumentId) return;
    const docId = currentDocumentId;
    const currentSeg = documents.find(d => d.id === docId)?.segments.find(s => s.id === id);
    const locked = options?.lock === true ? true : options?.lock === false ? false : status === "approved" ? true : currentSeg?.locked;
    setDocuments(prev => prev.map(doc => {
      if (doc.id !== docId) return doc;
      const updatedSegments = doc.segments.map(seg => seg.id === id ? {
        ...seg,
        status,
        mergedText: mergedText ?? seg.mergedText,
        locked
      } : seg);
      return {
        ...doc,
        segments: updatedSegments,
        status: deriveDocumentStatus(updatedSegments)
      };
    }));
    setDocumentStates(prev => {
      const existing = prev[docId] || [];
      const updated = existing.map(s => s.sectionId === id ? {
        ...s,
        status,
        mergedText: mergedText ?? s.mergedText,
        sourceText: currentSeg?.sourceText ?? s.sourceText,
        sourceHash: currentSeg?.sourceHash ?? s.sourceHash,
        locked: locked ?? s.locked
      } : s);
      const nextStates = updated;
      const next = {
        ...prev,
        [docId]: nextStates
      };
      queueMicrotask(() => persistDocumentState(docId, {
        segments: nextStates
      }));
      return next;
    });
  };
  const unlockSegment = (id: string) => {
    updateSegmentStatus(id, "in_progress", undefined, {
      lock: false
    });
  };
  const splitSection = (id: string, splitAt: number) => {
    if (!currentDocumentId) return;
    const docId = currentDocumentId;
    const seg = documents.find(d => d.id === docId)?.segments.find(s => s.id === id);
    if (!seg || seg.locked) return;
    if (splitAt <= 0 || splitAt >= seg.sourceText.length) return;
    const partA = seg.sourceText.slice(0, splitAt).trim();
    const partB = seg.sourceText.slice(splitAt).trim();
    if (!partA || !partB) return;
    setDocuments(prev => prev.map(doc => {
      if (doc.id !== docId) return doc;
      const idx = doc.segments.findIndex(s => s.id === id);
      if (idx < 0) return doc;
      const segA: Segment = {
        ...seg,
        sourceText: partA,
        sourceHash: hashSourceText(partA),
        status: "in_progress",
        mergedText: undefined,
        locked: false
      };
      const segB: Segment = {
        ...seg,
        id: `${seg.sectionId}-split-b`,
        sectionId: `${seg.sectionId}-split-b`,
        sourceText: partB,
        sourceHash: hashSourceText(partB),
        status: "not_started",
        mergedText: undefined,
        locked: false
      };
      const updated = [...doc.segments.slice(0, idx), segA, segB, ...doc.segments.slice(idx + 1)];
      return {
        ...doc,
        segments: updated
      };
    }));
  };
  const syncDocumentFromServer = useCallback(async (docId: string): Promise<DocSyncStats | null> => {
    const doc = documents.find(d => d.id === docId);
    if (!doc?.templateId) {
      throw new Error("Document not found or missing English template.");
    }
    const [freshBodies, storedRecord] = await Promise.all([fetchTemplateBodies(doc.templateId, doc.frTemplateId), fetchDocumentState(docId).catch(() => null)]);
    const stored = storedRecord?.segments || documentStates[docId] || [];
    const draftCache = storedRecord?.draftCache || draftCaches[docId] || {};
    const parsedEn = parseLexicalBody(freshBodies.enBody);
    const enSegments = parsedEn.filter(s => s.isTranslatable);
    const frMatches = matchTranslations(parsedEn, freshBodies.frBody);
    const {
      segments,
      states,
      report
    } = syncSegmentsWithStored(enSegments, stored, frMatches);
    const activeStates = states.filter(s => s.status !== "removed");
    const activeSegments = segments.filter(s => s.status !== "removed");
    const stats = reportToDocStats(report);
    setDocumentStates(prev => ({
      ...prev,
      [docId]: activeStates
    }));
    setDocuments(prev => prev.map(d => d.id === docId ? {
      ...d,
      enBody: freshBodies.enBody,
      frBody: freshBodies.frBody,
      segments: activeSegments,
      status: deriveDocumentStatus(segments)
    } : d));
    try {
      const record = await upsertDocumentState({
        documentPairKey: docId,
        enTemplateId: doc.templateId,
        frTemplateId: doc.frTemplateId,
        segments: activeStates,
        draftCache
      });
      stateRecordsRef.current[docId] = record;
    } catch (err) {
      console.warn(`Failed to persist sync results for ${docId}:`, err);
    }
    setLastSyncReport(report);
    return stats;
  }, [documents, documentStates, draftCaches]);
  const refreshDocuments = useCallback(async () => {
    const statsMap = await loadDocuments();
    if (currentDocumentId) {
      const report = statsMap.get(currentDocumentId);
      if (report) setLastSyncReport(report);
    }
    return statsMap;
  }, [loadDocuments, currentDocumentId]);
  const addDocument = (title: string, sourceText: string) => {
    const rawSegments = sourceText.split(/\n\s*\n/).filter(text => text.trim().length > 0);
    const newSegments: Segment[] = rawSegments.map((text, index) => ({
      id: `seg-new-${Date.now()}-${index}`,
      sectionId: `seg-new-${Date.now()}-${index}`,
      structuralPath: `manual-${index}`,
      sourceHash: hashSourceText(text.trim()),
      clauseId: `Section ${index + 1}`,
      sourceText: text.trim(),
      status: "in_progress"
    }));
    const newDoc: Document = {
      id: `doc-${Date.now()}`,
      title,
      createdAt: new Date().toISOString(),
      sourceLanguage: "EN",
      targetLanguage: "FR",
      segments: newSegments,
      status: "in_review",
      isActive: true
    };
    setDocuments(prev => [newDoc, ...prev]);
    return newDoc.id;
  };
  const addGlossaryEntry = (entry: Omit<GlossaryEntry, "id">) => {
    setGlossary(prev => [...prev, {
      ...entry,
      id: `g${Date.now()}`
    }]);
  };
  const updateGlossaryEntry = (id: string, entry: Partial<GlossaryEntry>) => {
    setGlossary(prev => prev.map(g => g.id === id ? {
      ...g,
      ...entry
    } : g));
  };
  const deleteGlossaryEntry = (id: string) => {
    setGlossary(prev => prev.filter(g => g.id !== id));
  };
  const setGlossaryFromBackup = (entries: GlossaryEntry[]) => {
    setGlossary(entries);
  };
  const setSegmentOverridesFromBackup = (overrides: Record<string, {
    status: SegmentStatus;
    mergedText?: string;
    sourceText?: string;
  }>) => {
    const byDoc: Record<string, StoredSegmentState[]> = {};
    for (const doc of documents) {
      byDoc[doc.id] = storedStatesFromLegacyOverrides(doc.id, doc.segments, overrides);
    }
    setDocumentStates(prev => ({
      ...prev,
      ...byDoc
    }));
    for (const docId of Object.keys(byDoc)) {
      persistDocumentState(docId);
    }
    loadDocuments();
  };
  const restoreDocumentStatesFromBackup = (states: Record<string, StoredSegmentState[]>) => {
    setDocumentStates(prev => ({
      ...prev,
      ...states
    }));
    for (const docId of Object.keys(states)) {
      persistDocumentState(docId);
    }
    loadDocuments();
  };
  const bulkApproveTranslatedTemplates = useCallback(async (pairs: TranslatedTemplatePair[], options?: {
    onProgress?: (info: {
      current: number;
      total: number;
      title: string;
    }) => void;
  }) => {
    if (!pairs?.length) {
      return {
        total: 0,
        succeeded: 0,
        failed: 0,
        approvedSegments: 0,
        items: []
      };
    }
    const report = await bulkApproveTranslatedTemplatePairs(pairs, options);
    await loadDocuments();
    return report;
  }, [loadDocuments]);
  const saveDraftCacheForDocument = useCallback(async (docId: string, nextDraftCaches: Record<string, Record<string, unknown>>) => {
    const doc = documents.find(d => d.id === docId);
    if (!doc?.templateId) return;
    const states = documentStatesRef.current[docId] ?? [];
    try {
      const record = await upsertDocumentState({
        documentPairKey: docId,
        enTemplateId: doc.templateId,
        frTemplateId: doc.frTemplateId,
        segments: states.filter(s => s.status !== "removed"),
        draftCache: nextDraftCaches[docId] || {}
      });
      stateRecordsRef.current[docId] = record;
    } catch (err) {
      console.warn(`Failed to persist draft cache for ${docId}:`, err);
    }
  }, [documents, draftCaches]);
  const getSegmentDraftCacheForCurrentDoc = useCallback((segmentId: string) => {
    if (!currentDocumentId) return null;
    return getSegmentDraftCache(draftCaches, currentDocumentId, segmentId);
  }, [currentDocumentId, draftCaches]);
  const saveSegmentDraftCacheForCurrentDoc = useCallback((segmentId: string, work: CachedSegmentWork) => {
    if (!currentDocumentId) return;
    setDraftCaches(prev => {
      const nextDraftCaches = withSegmentDraftCache(prev, currentDocumentId, segmentId, work);
      queueMicrotask(() => saveDraftCacheForDocument(currentDocumentId, nextDraftCaches));
      return nextDraftCaches;
    });
  }, [currentDocumentId, saveDraftCacheForDocument]);
  const clearSegmentDraftCacheForCurrentDoc = useCallback((segmentId: string) => {
    if (!currentDocumentId) return;
    setDraftCaches(prev => {
      const nextDraftCaches = withoutSegmentDraftCache(prev, currentDocumentId, segmentId);
      queueMicrotask(() => saveDraftCacheForDocument(currentDocumentId, nextDraftCaches));
      return nextDraftCaches;
    });
  }, [currentDocumentId, saveDraftCacheForDocument]);
  const clearAllTranslations = async () => {
    setDocumentStates({});
    setDraftCaches({});
    for (const doc of documents) {
      if (doc.templateId) {
        await upsertDocumentState({
          documentPairKey: doc.id,
          enTemplateId: doc.templateId,
          frTemplateId: doc.frTemplateId,
          segments: [],
          draftCache: {}
        });
      }
    }
    loadDocuments();
  };
  const segmentOverridesFlat: Record<string, StoredSegmentState> = {};
  for (const [docId, states] of Object.entries(documentStates)) {
    for (const s of states) {
      segmentOverridesFlat[`${docId}-${s.sectionId}`] = s;
    }
  }
  return <TranslationContext.Provider value={{
    documents,
    currentDocument,
    setCurrentDocumentId,
    segments,
    glossary,
    updateSegmentStatus,
    unlockSegment,
    splitSection,
    addGlossaryEntry,
    updateGlossaryEntry,
    deleteGlossaryEntry,
    documentTitle,
    addDocument,
    isLoading,
    error,
    refreshDocuments,
    syncDocumentFromServer,
    lastSyncReport,
    openRouterApiKey,
    setOpenRouterApiKey: handleSetApiKey,
    modelConfigs,
    setModelConfigs: handleSetModelConfigs,
    setGlossaryFromBackup,
    segmentOverrides: segmentOverridesFlat,
    setSegmentOverridesFromBackup,
    restoreDocumentStatesFromBackup,
    bulkApproveTranslatedTemplates,
    getSegmentDraftCache: getSegmentDraftCacheForCurrentDoc,
    saveSegmentDraftCache: saveSegmentDraftCacheForCurrentDoc,
    clearSegmentDraftCache: clearSegmentDraftCacheForCurrentDoc,
    clearAllTranslations,
    documentStates,
    persistDocumentState
  }} data-id="element-242">
      {children}
    </TranslationContext.Provider>;
};
export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (context === undefined) {
    throw new Error("useTranslation must be used within a TranslationProvider");
  }
  return context;
};

// Re-export for injection helper used by hub/workspace pages
export { segmentsToInjectionTexts };